import React, { useState, useEffect, Fragment } from 'react';
import Drawer from '@material-ui/core/Drawer';
import { connect } from 'react-redux';
import lodash, { isEmpty } from 'lodash';
import Input from 'components/common/Input/FormInput';
import InputField from 'components/common/Study/InputField';
import { StudyUsersAddMonitorForm } from './StudyUsersAddMonitorForm';
import { Button, BUTTON } from 'components/common/Button/Button';
import {
    loadExternalUserForStudy,
    clearExternalUserForStudy,
} from 'pages/studyOverview/actions/studySettings';
import { resetUserPassword } from 'part11/actions/users';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrashAlt, faCheck } from '@fortawesome/free-solid-svg-icons';
import styles from './AddMonitorModal.scss';
import { loadHomePageV4Access } from '../../../../common/features/predicates';

const EMAIL_ADDRESS_PATTERN =
    /^[a-z0-9!#$%&'*+/=?^_‘{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_‘{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const AddMonitorModal = (props) => {
    const {
        clearExternalUserForStudy,
        dismiss,
        existingExternalUser,
        loadExternalUserForStudy,
        onAction,
        editMonitor,
        onDismiss,
        removeMonitor,
        searchingStudyMonitors,
        resetUserPassword,
        canManageExternalUserAccess,
        complion4FlagEnabled,
    } = props;

    const existingUser = lodash.isEmpty(editMonitor) ? existingExternalUser : editMonitor;
    const [externalUser, setExternalUser] = useState({});
    const [inputFieldsComplete, setInputFieldsComplete] = useState(false);
    const [submitBtnClicked, setSubmitBtnClicked] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerContent, setDrawerContent] = useState(null);
    const [isResetDone, setIsResetDone] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [email, setEmail] = useState('');

    useEffect(() => {
        setExternalUser(existingUser);
    }, [existingExternalUser, editMonitor]);


    useEffect(() => {
        if (!isEmpty(externalUser)) {
            setStartDate(externalUser['startDate']);
            setEndDate(externalUser['endDate']);
            setStartTime(externalUser['startTime']);
            setEndTime(externalUser['endTime']);
            setEmail(externalUser['emailAddress']);
        }
    }, [externalUser]);

    useEffect(() => {
        allFieldValues();
    }, [externalUser]);

    const checkIfMonitorIsExistingUser = (value) =>
        loadExternalUserForStudy(value);

    const updateExternalUserInputs = (key, value) => {
        setExternalUser(prevState => ({
            ...prevState,
            [key]: value
        }));

        allFieldValues();
    };

    const isValid = (name) => !lodash.isEmpty(externalUser[name]);

    const endDateIsValid = () => {
        if (startDate && endDate) {
            const startDateIsGreaterThanEndDate = startDate > endDate;

            if (startDateIsGreaterThanEndDate) {
                return false;
            }
        }

        return true;
    };

    const endTimeIsValid = () => {
        if (startDate && startTime && endDate && endTime) {
            const startTimeIsGreaterThanOrEqualToEndTime = startTime >= endTime;

            const startDateEqualsEndDate = startDate === endDate;

            if (startDateEqualsEndDate && startTimeIsGreaterThanOrEqualToEndTime) {
                return false;
            }
        }

        return true;
    };

    const allFieldValues = () => {
        if (
            isValid('emailAddress') &&
            isValid('firstName') &&
            isValid('lastName') &&
            isValid('startDate') &&
            isValid('startTime') &&
            endDateIsValid() &&
            endTimeIsValid()
        ) {
            setInputFieldsComplete(true);
        } else {
            setInputFieldsComplete(false);
        }
    };

    const dismissAll = () => {
        clearExternalUserForStudy();
        setExternalUser({});
        onDismiss();
    };

    const isExistingExternalUser =
        !lodash.isEmpty(existingExternalUser) && !searchingStudyMonitors;

    const handleNextAction = () => {
        checkIfMonitorIsExistingUser(externalUser.emailAddress);
    };

    const updateEmailState = (e) => {
        const email = e.target.value.toLowerCase();

        setExternalUser(prevState => ({
            ...prevState,
            emailAddress: email
        }));
    };

    const updateEmailStateOnBlur = (e) => {
        const email = e.target.value.toLowerCase();
        setExternalUser(prevState => ({
            ...prevState,
            emailAddress: email
        }));
        checkIfMonitorIsExistingUser(email);
    };

    const emailMatchesExpectedPattern = () => {
        return EMAIL_ADDRESS_PATTERN.test(externalUser.emailAddress);
    };

    const missingRequiredFields = () => {
        return !externalUser.emailAddress;
    };

    const allInputFieldsComplete = () => {
        if (submitBtnClicked) {
            return !missingRequiredFields() && emailMatchesExpectedPattern();
        } else {
            return true;
        }
    };

    const handleSubmit = () => {
        setSubmitBtnClicked(true);
        allFieldValues();
        if (inputFieldsComplete && emailMatchesExpectedPattern()) {
            onAction(externalUser);
            dismissAll();
        }
    };

    const openDrawer = (content) => {
        setDrawerContent(content);
        setIsDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerContent(null);
        setIsDrawerOpen(false);
    };

    const handleRemoveAccount = () => {
        openDrawer(
            <div className={styles.emptyDrawerContent}>
                <p>Are you sure you want to remove {externalUser.displayName} from this {complion4FlagEnabled ? 'Binder' : 'Study'}?</p>
                <div className={styles.buttonContainer}>
                    <Button
                        bgcolor={BUTTON.BGCOLOR.CLEAR}
                        color={BUTTON.COLOR.BLACK}
                        outline={BUTTON.OUTCOLOR.GREY}
                        onClick={handleRemoveConfirmed}
                    >
                        Yes
                    </Button>
                    <Button
                        bgcolor={BUTTON.BGCOLOR.CLEAR}
                        color={BUTTON.COLOR.BLACK}
                        outline={BUTTON.OUTCOLOR.GREY}
                        onClick={closeDrawer}
                    >
                        No
                    </Button>
                </div>
            </div>
        );
    };

    const handleResetAccount = () => {
        openDrawer(
            <div className={styles.emptyDrawerContent}>
                <p>Are you sure you want to reset {externalUser.displayName}’s password?</p>
                <div className={styles.buttonContainer}>
                    <Button
                        bgcolor={BUTTON.BGCOLOR.CLEAR}
                        color={BUTTON.COLOR.BLACK}
                        outline={BUTTON.OUTCOLOR.GREY}
                        onClick={handleResetAccountConfirmed}
                    >
                        Yes
                    </Button>
                    <Button
                        bgcolor={BUTTON.BGCOLOR.CLEAR}
                        color={BUTTON.COLOR.BLACK}
                        outline={BUTTON.OUTCOLOR.GREY}
                        onClick={closeDrawer}
                    >
                        No
                    </Button>
                </div>
            </div>
        );
    };

    const handleRemoveConfirmed = () => {
        removeMonitor(externalUser);
        dismissAll();
        closeDrawer();
    };

    const handleResetAccountConfirmed = () => {
        resetUserPassword(externalUser);
        setIsResetDone(true);
        closeDrawer();
    };

    const isEditMode = !lodash.isEmpty(editMonitor);

    return (
        <div>
            {isDrawerOpen && (
                <Drawer open={true} anchor='right' onClose={closeDrawer}>
                    <div className={styles.drawerContainer}>
                        {drawerContent}
                    </div>
                </Drawer>
            )}

            <Drawer open={dismiss} anchor='right' onClose={dismissAll}>
                <div className={styles.addMonitorModalContainer}>
                    <div className={styles.addMonitorModalHeader}>
                        <div className={styles.addMonitorModalHeaderContent}>
                            <div className={styles.addMonitorModalHeaderContentText}>
                                {isEditMode ? 'Edit Monitor' : 'Add Monitor'}
                            </div>
                            <div className={styles.addMonitorModalHeaderContentIcon}>
                                <FontAwesomeIcon icon={faTimes} className={styles.addMonitorModalHeaderCloseIcon} onClick={dismissAll} />
                            </div>
                        </div>
                        <hr className={styles.divider} />
                    </div>
                    <div className={styles.addMonitorModalBody}>
                        {isEditMode ?
                            < Fragment >
                                <Field
                                    label='Email Address'
                                    value={email}
                                />
                                <hr />
                            </Fragment>
                            : (<InputField required={false}>
                                <Input
                                    title='Email Address'
                                    value={email}
                                    placeholder='email@example.com'
                                    required={true}
                                    isValid={allInputFieldsComplete}
                                    errorText='A valid Email Address is required'
                                    onBlur={updateEmailStateOnBlur}
                                    onChange={updateEmailState}
                                    withBorder={true}
                                    autoFocus={true}
                                    onClick={handleNextAction}
                                    submitClicked={submitBtnClicked}
                                    data-cy='AddMonitorModal-Email'
                                />
                            </InputField>)}
                        <StudyUsersAddMonitorForm
                            editMode={isEditMode}
                            externalUser={externalUser}
                            existingUser={existingUser}
                            isExistingExternalUser={isExistingExternalUser}
                            updateExternalUserInputs={updateExternalUserInputs}
                            endDateIsValid={endDateIsValid}
                            endTimeIsValid={endTimeIsValid}
                            submitBtnClicked={submitBtnClicked}
                            setSubmitBtnClicked={setSubmitBtnClicked}
                            complion4FlagEnabled={complion4FlagEnabled}
                        />
                    </div>
                    <div className={styles.addMonitorrModalFooter}>
                        <hr className={styles.divider} />
                        <div className={styles.addMonitorModalFooterContent}>
                            {isEditMode && (
                                <div className={styles.addMonitorModalFooterContentLeft}>
                                    {canManageExternalUserAccess && (
                                        <Button
                                            bgcolor={BUTTON.BGCOLOR.CLEAR}
                                            color={BUTTON.COLOR.RED}
                                            className={styles.removeButton}
                                            data-cy='AddMonitorModal-Remove'
                                            onClick={handleRemoveAccount}
                                        >
                                            <FontAwesomeIcon icon={faTrashAlt} className={styles.trashIcon} />
                                            <p className={styles.removePara}>Remove from {complion4FlagEnabled ? 'Binder' : 'Study'}</p>
                                        </Button>
                                    )}

                                    {isResetDone ? (
                                        <p className={styles.resetDoneText}>
                                            <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                                            Password Reset
                                        </p>
                                    ) : (
                                        canManageExternalUserAccess && (
                                            <Button
                                                className={styles.btnLayout}
                                                type={BUTTON.SIZE.SECONDARY}
                                                bgcolor={BUTTON.BGCOLOR.CLEAR}
                                                color={BUTTON.COLOR.BLACK}
                                                onClick={handleResetAccount}
                                                outline={BUTTON.OUTCOLOR.GREY}
                                                data-cy='AddMonitorModal-ResetAccount'
                                            >
                                                Reset Password
                                            </Button>
                                        )
                                    )}
                                </div>)}

                            <div className={styles.addMonitorModalFooterContentRight}>
                                <Button
                                    className={styles.btnLayout}
                                    type={BUTTON.SIZE.SECONDARY}
                                    bgcolor={BUTTON.BGCOLOR.CLEAR}
                                    color={BUTTON.COLOR.BLACK}
                                    onClick={dismissAll}
                                    outline={BUTTON.OUTCOLOR.GREY}
                                    data-cy='AddMonitorModal-Cancel'
                                >
                                    Cancel
                                </Button>

                                <Button
                                    className={styles.btnLayout}
                                    bgcolor={BUTTON.BGCOLOR.ORANGE}
                                    color={BUTTON.COLOR.WHITE}
                                    onClick={handleSubmit}
                                    data-cy='AddMonitorModal-AddMonitor'
                                >
                                    {isEditMode ? 'Save' : 'Add Monitor'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

const Field = ({ label, value }) => (
    <div className={styles.field}>
        <h2 className={styles.label}>{label}</h2>
        <h2 className={styles.value}>{value}</h2>
    </div>
);

export default connect(
    (state) => {
        const existingExternalUser = lodash.get(state, 'existingExternalUser', {});
        const searchingStudyMonitors = lodash.get(
            state,
            'existingExternalUser.loading',
            false
        );
        const studyMonitors = lodash.get(state, 'externalUsersForStudy', []);
        const canManageExternalUserAccess = lodash.get(state, 'me.binderPermissions.manageExternalUserAccess', false);

        return {
            existingExternalUser: formatExistingExternalUserDates(
                existingExternalUser,
                studyMonitors
            ),
            searchingStudyMonitors,
            canManageExternalUserAccess,
            complion4FlagEnabled: loadHomePageV4Access(state),
        };
    },
    {
        loadExternalUserForStudy,
        clearExternalUserForStudy,
        resetUserPassword,
    }
)(AddMonitorModal);

function formatExistingExternalUserDates(existingExternalUser, studyMonitors) {
    const existingStudyMonitor = studyMonitors.find(
        (studyMonitor) => studyMonitor.id === existingExternalUser.id
    );

    return existingStudyMonitor || existingExternalUser;
}
